<script setup lang="ts">
import { useAppStore } from "@/store/app";
import type { IntegratePane } from "@/util/embedCode";
import {
  Code02Icon,
  Link02Icon,
  Mail01Icon,
  Share07Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const store = useAppStore();
const { t } = useI18n();
const items = computed(() => [
  { key: "embed" as const, label: t("common.embed"), icon: Code02Icon },
  { key: "share" as const, label: t("common.share"), icon: Share07Icon },
  ...(store.formId
    ? [{ key: "email" as const, label: t("common.email"), icon: Mail01Icon }]
    : []),
  {
    key: "connections" as const,
    label: t("common.connections"),
    icon: Link02Icon,
  },
]);

const select = (key: IntegratePane) => {
  store.integratePane = key;
};
</script>

<template>
  <nav class="integrate-nav" :aria-label="t('common.integrate')">
    <div
      class="integrate-nav__list"
      role="tablist"
      :aria-label="t('common.integrate')"
    >
      <UIButton
        v-for="item in items"
        :id="`integrate-nav-tab-${item.key}`"
        :key="item.key"
        :data-testid="`integrate-nav-${item.key}`"
        :class="[
          'integrate-nav__item',
          { 'integrate-nav__item--active': store.integratePane === item.key },
        ]"
        text
        role="tab"
        :aria-selected="store.integratePane === item.key"
        @click="select(item.key)"
      >
        <component :is="item.icon" class="h-4 w-4" />
        <span class="integrate-nav__label">{{ item.label }}</span>
      </UIButton>
    </div>
  </nav>
</template>

<style scoped>
.integrate-nav {
  padding: 0;
  background: transparent;
}
.integrate-nav__list {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
}
.integrate-nav__item {
  --n-color: transparent !important;
  --n-color-hover: transparent !important;
  --n-color-focus: transparent !important;
  --n-color-pressed: transparent !important;
  --n-text-color: var(--gray-700, #344054) !important;
  --n-text-color-hover: var(--gray-700, #344054) !important;
  --n-text-color-focus: var(--gray-700, #344054) !important;
  --n-text-color-pressed: var(--gray-700, #344054) !important;
  display: flex;
  width: 100%;
  min-height: 40px;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 0 !important;
  border-radius: 6px;
  background: transparent !important;
  box-shadow: none !important;
  color: var(--gray-700, #344054) !important;
}
.integrate-nav__item--active,
.integrate-nav__item--active:hover,
.integrate-nav__item--active:focus {
  --n-text-color: var(--primary-600, #155eef) !important;
  --n-text-color-hover: var(--primary-600, #155eef) !important;
  --n-text-color-focus: var(--primary-600, #155eef) !important;
  background: var(--base-white, #fff) !important;
  box-shadow: var(--shadow-xs, 0 1px 2px rgb(16 24 40 / 5%));
  color: var(--primary-600, #155eef) !important;
}
.integrate-nav__item:focus-visible {
  outline: 2px solid var(--primary-600, #155eef);
  outline-offset: 2px;
}
.integrate-nav__item :deep(.n-button__content) {
  width: 100%;
  justify-content: flex-start;
  gap: 8px;
}
.integrate-nav__label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
</style>
